import { FC, useEffect, useMemo, useState } from "react";

import {
	Box,
	Card,
	CardActionArea,
	Chip,
	Stack,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { SATOSLink } from "@/components/Application/SATOSLink";
import { useTime } from "@/components/Application/TimeContext";
import { NodeDiagramButton } from "@/components/Procedures/NodeDiagram/utils/NodeDiagramButton";

import CircularProgressWithLabel from "@/helpers/components/CircularProgressWithLabel";

import { ActiveProcedure } from "@/model/Procedure/ActiveProcedure";

dayjs.extend(utc);

type ActiveProcedureItemProps = { item: ActiveProcedure };

export const ActiveProcedureItem: FC<ActiveProcedureItemProps> = ({ item }) => {
	const theme = useTheme();
	const { timezone } = useTime();

	const [pct, setPct] = useState<number>(0);

	useEffect(() => {
		const total = Number(item.TotalTasks) || 0;
		const done = Number(item.NumTasks) || 0;
		const value =
			total > 0 ? Math.min(100, Math.max(0, (done / total) * 100)) : 0;
		setPct(Math.round(value));
	}, [item.TotalTasks, item.NumTasks]);

	// Map % to a smooth red→orange progression; green only at 100%
	const bandColor = useMemo(() => {
		const { error, warning, success } = theme.palette;
		if (pct >= 100) return success.main; // green only at done
		if (pct >= 75) return warning.main; // lighter orange
		if (pct >= 50) return warning.dark; // darker orange
		if (pct >= 25) return error.main; // lighter red
		return error.dark; // dark red
	}, [pct, theme.palette]);

	const lastModified = useMemo(
		() =>
			dayjs.utc(item.LastModified).tz(timezone).format("DD/MM/YY, HH:mm:ss"),
		[item.LastModified, timezone],
	);

	return (
		<SATOSLink
			to="AccessProcedure"
			params={[item.Id]}
			style={{ textDecoration: "none", color: "inherit" }}
		>
			<Card
				variant="outlined"
				sx={(t) => ({
					mb: 1,
					position: "relative",
					borderRadius: 2,
					overflow: "hidden",
					borderColor: alpha(bandColor, 0.3),
					transition:
						"box-shadow .18s ease, border-color .18s ease, transform .18s ease",
					"&:hover": {
						transform: "translateY(-2px)",
						boxShadow: "0 10px 28px rgba(0,0,0,.08)",
						borderColor: alpha(bandColor, 0.55),
					},
				})}
			>
				{/* Left accent ticks (remember the picture) */}
				<Box
					aria-hidden
					sx={{
						position: "absolute",
						left: 6,
						top: 8,
						bottom: 8,
						width: 8,
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "space-between",
						pointerEvents: "none",
					}}
				>
					<Box
						sx={{
							width: 6,
							height: 14,
							borderRadius: 6,
							bgcolor: alpha(bandColor, 0.35),
						}}
					/>
					<Box
						sx={{
							width: 6,
							height: 18,
							borderRadius: 6,
							bgcolor: alpha(bandColor, 0.8),
						}}
					/>
					<Box
						sx={{
							width: 6,
							height: 14,
							borderRadius: 6,
							bgcolor: alpha(bandColor, 0.35),
						}}
					/>
				</Box>

				<CardActionArea sx={{ p: 1.25, pl: 2.5 /* space for ticks */ }}>
					<Stack direction="row" spacing={1.5} alignItems="center">
						{/* Progress */}
						<Box
							sx={{
								minWidth: 56,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<CircularProgressWithLabel value={pct} type="%" />
						</Box>

						{/* Title + time */}
						<Box sx={{ flex: 1, minWidth: 0 }}>
							<Tooltip title={item.Name} enterDelay={600}>
								<Typography variant="subtitle2" fontWeight={800} noWrap>
									{item.Name}
								</Typography>
							</Tooltip>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ display: "block", mt: 0.25 }}
							>
								{lastModified}
							</Typography>
						</Box>

						{/* Right-side actions */}
						<Stack direction="row" spacing={1} alignItems="center">
							<Chip
								size="small"
								variant="outlined"
								label={`${item.NumTasks ?? 0}/${item.TotalTasks ?? 0}`}
								sx={{
									fontWeight: 700,
									"& .MuiChip-label": { px: 0.75 },
									// use the computed bandColor for text  border so shades show
									color: bandColor,
									borderColor: bandColor,
								}}
							/>
							{/* Prevent outer link navigation when clicking the diagram button */}
							<Box
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
								}}
							>
								<NodeDiagramButton item={item} />
							</Box>
						</Stack>
					</Stack>
				</CardActionArea>
			</Card>
		</SATOSLink>
	);
};
